import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, ChevronLeft, ChevronRight, ChevronDown, ArrowRight, Watch, Gem } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ProductCard } from "@/components/store/ProductCard";
import { CATEGORIES } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "best_selling", label: "Best Selling" },
  { value: "rating", label: "Top Rated" },
  { value: "name", label: "Alphabetical" },
];

const GENDER_CATS = {
  men: [
    { label: "Earrings", category: "earrings" },
    { label: "Chains", category: "necklaces" },
    { label: "Bracelets", category: "bracelets" },
    { label: "Rings", category: "rings" },
  ],
  women: [
    { label: "Earrings", category: "earrings" },
    { label: "Necklaces", category: "necklaces" },
    { label: "Bracelets", category: "bracelets" },
    { label: "Rings", category: "rings" },
  ],
};

const KARATS = [
  { value: "18k", label: "18k Gold" },
  { value: "14k", label: "14k Gold" },
  { value: "10k", label: "10k Gold" },
];

const PRICE_MIN = 100;
const PRICE_MAX = 1000000;
const clampPrice = (n) => Math.min(PRICE_MAX, Math.max(PRICE_MIN, isNaN(n) ? PRICE_MIN : n));

const FilterGroup = ({ title, children }) => (
  <div className="py-7 border-b border-border/70">
    <h4 className="overline text-gold mb-5">{title}</h4>
    {children}
  </div>
);

export default function Catalog() {
  const { category: routeCategory, name: routeCollection } = useParams();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const genderParam = searchParams.get("gender") || "";
  const queryCategory = searchParams.get("category") || "";
  const initialBest = searchParams.get("best_seller") === "true";
  const initialNew = searchParams.get("new_arrival") === "true";

  const [meta, setMeta] = useState({ has_sale: false, min_price: 0, max_price: 12000 });
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [gender, setGender] = useState(genderParam);
  const [category, setCategory] = useState(routeCategory || queryCategory || "all");
  const [collection, setCollection] = useState(routeCollection || "all");
  const [karats, setKarats] = useState([]);
  const [priceRange, setPriceRange] = useState([PRICE_MIN, PRICE_MAX]);
  const [priceDraft, setPriceDraft] = useState([PRICE_MIN, PRICE_MAX]);
  const [onSale, setOnSale] = useState(false);
  const [sort, setSort] = useState("featured");
  const [page, setPage] = useState(1);
  const [mobileFilters, setMobileFilters] = useState(false);

  const isWatches = category === "watches";

  useEffect(() => {
    setCategory(routeCategory || queryCategory || "all");
    setPage(1);
  }, [routeCategory, queryCategory]);

  useEffect(() => {
    setGender(genderParam);
    setPage(1);
  }, [genderParam]);

  useEffect(() => {
    setCollection(routeCollection || "all");
    setPage(1);
  }, [routeCollection]);

  useEffect(() => {
    api.get("/products/filters").then((r) => {
      setMeta(r.data);
    }).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const params = { sort, page, limit: 12 };
    if (category && category !== "all") params.category = category;
    if (gender && !isWatches) params.gender = gender;
    if (collection && collection !== "all") params.collection = collection;
    if (karats.length) params.karat = karats.join(",");
    if (priceRange) { params.min_price = priceRange[0]; params.max_price = priceRange[1]; }
    if (onSale) params.on_sale = true;
    if (search) params.search = search;
    if (initialBest) params.best_seller = true;
    if (initialNew) params.new_arrival = true;
    try {
      const { data } = await api.get("/products", { params });
      setProducts(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } finally {
      setLoading(false);
    }
  }, [category, gender, isWatches, collection, karats, priceRange, onSale, sort, page, search, initialBest, initialNew]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const title = useMemo(() => {
    if (search) return `${t("Search:")} “${search}”`;
    if (routeCollection) return routeCollection;
    if (isWatches) return "Watches";
    const catName = category && category !== "all" ? CATEGORIES.find((c) => c.slug === category)?.name : null;
    if (gender) {
      const g = gender === "men" ? t("Men") : gender === "women" ? t("Women") : t("Unisex");
      return catName ? `${g} · ${t(catName)}` : g;
    }
    if (catName) return catName;
    if (initialBest) return "Best Sellers";
    if (initialNew) return "New Arrivals";
    return "All Jewelry";
  }, [search, routeCollection, isWatches, initialBest, initialNew, gender, category, t]);

  // --- filter interactions -------------------------------------------------
  const pickGender = (g) => {
    setGender((prev) => (prev === g ? "" : g));
    if (isWatches) setCategory("all"); // watches & gender are mutually exclusive
    else setCategory("all");
    setPage(1);
  };

  const pickSubCategory = (cat) => {
    setCategory((prev) => (prev === cat ? "all" : cat));
    setPage(1);
  };

  const toggleWatches = () => {
    if (isWatches) {
      setCategory("all");
    } else {
      setCategory("watches");
      setGender("");
    }
    setPage(1);
  };

  const toggleKarat = (val) => {
    setKarats((prev) => (prev.includes(val) ? prev.filter((k) => k !== val) : [...prev, val]));
    setPage(1);
  };

  const commitPrice = () => {
    let lo = clampPrice(Number(priceDraft[0]));
    let hi = clampPrice(Number(priceDraft[1]));
    if (lo > hi) [lo, hi] = [hi, lo];
    setPriceDraft([lo, hi]);
    setPriceRange([lo, hi]);
    setPage(1);
  };

  const clearAll = () => {
    setKarats([]); setOnSale(false);
    if (!routeCategory) { setCategory("all"); setGender(""); }
    if (!routeCollection) setCollection("all");
    setPriceDraft([PRICE_MIN, PRICE_MAX]);
    setPriceRange([PRICE_MIN, PRICE_MAX]);
    setPage(1);
  };

  const activeCount =
    (gender ? 1 : 0) + (isWatches ? 1 : 0) + karats.length + (onSale ? 1 : 0) +
    (priceRange[0] !== PRICE_MIN || priceRange[1] !== PRICE_MAX ? 1 : 0) +
    (!gender && !isWatches && category !== "all" ? 1 : 0);

  const filtersContent = (
    <div data-testid="filter-panel">
      {/* BROWSE: gender / watches / accessories */}
      <FilterGroup title={t("Browse")}>
        <div className="grid grid-cols-2 gap-2 mb-2">
          {["men", "women"].map((g) => (
            <button
              key={g}
              onClick={() => pickGender(g)}
              data-testid={`filter-gender-${g}`}
              disabled={isWatches}
              className={`h-11 text-xs uppercase tracking-[0.18em] border transition-all disabled:opacity-30 disabled:cursor-not-allowed ${
                gender === g
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:border-gold hover:text-gold"
              }`}
            >
              {g === "men" ? t("Men") : t("Women")}
            </button>
          ))}
        </div>

        {gender && !isWatches && (
          <div className="pl-1 pt-2 flex flex-col border-l border-gold/40 ml-1">
            {GENDER_CATS[gender].map((it) => (
              <button
                key={it.category}
                onClick={() => pickSubCategory(it.category)}
                data-testid={`filter-subcat-${it.category}`}
                className={`py-2.5 pl-4 text-sm text-left transition-colors ${
                  category === it.category ? "text-gold font-medium" : "text-foreground/60 hover:text-gold"
                }`}
              >
                {t(it.label)}
              </button>
            ))}
          </div>
        )}

        <button
          onClick={toggleWatches}
          data-testid="filter-watches"
          className={`flex items-center gap-3 w-full h-11 mt-3 px-3 border text-xs uppercase tracking-[0.18em] transition-all ${
            isWatches
              ? "bg-primary text-primary-foreground border-primary"
              : "border-border hover:border-gold hover:text-gold"
          }`}
        >
          <Watch className="h-4 w-4" /> {t("Watches")}
        </button>

        <Link
          to="/accessories"
          data-testid="filter-accessories"
          className="flex items-center justify-between gap-3 w-full h-11 mt-2 px-3 border border-border text-xs uppercase tracking-[0.18em] text-foreground/70 hover:border-gold hover:text-gold transition-all group"
        >
          <span className="flex items-center gap-3"><Gem className="h-4 w-4" /> {t("Accessories")}</span>
          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </FilterGroup>

      {/* PRICE */}
      <FilterGroup title={t("Price")}>
        <div className="px-1">
          <Slider
            value={priceDraft}
            min={PRICE_MIN}
            max={PRICE_MAX}
            step={100}
            onValueChange={setPriceDraft}
            onValueCommit={commitPrice}
            className="my-4"
            data-testid="price-slider"
          />
          <div className="flex items-center gap-2 mt-5">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground block mb-1.5">{t("Min")}</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">$</span>
                <Input
                  type="number"
                  min={PRICE_MIN}
                  max={PRICE_MAX}
                  value={priceDraft[0]}
                  onChange={(e) => { const v = e.target.value === "" ? "" : Number(e.target.value); setPriceDraft((p) => [v, p[1]]); }}
                  onBlur={commitPrice}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  className="rounded-none h-10 pl-6 text-sm"
                  data-testid="price-min-input"
                />
              </div>
            </div>
            <span className="text-muted-foreground pt-5">—</span>
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground block mb-1.5">{t("Max")}</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">$</span>
                <Input
                  type="number"
                  min={PRICE_MIN}
                  max={PRICE_MAX}
                  value={priceDraft[1]}
                  onChange={(e) => { const v = e.target.value === "" ? "" : Number(e.target.value); setPriceDraft((p) => [p[0], v]); }}
                  onBlur={commitPrice}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  className="rounded-none h-10 pl-6 text-sm"
                  data-testid="price-max-input"
                />
              </div>
            </div>
          </div>
        </div>
      </FilterGroup>

      {/* GOLD TYPE */}
      <FilterGroup title={t("Gold Type")}>
        <div className="space-y-3">
          {KARATS.map((k) => (
            <label key={k.value} className="flex items-center gap-3 cursor-pointer text-sm">
              <Checkbox
                checked={karats.includes(k.value)}
                onCheckedChange={() => toggleKarat(k.value)}
                data-testid={`filter-karat-${k.value}`}
              />
              {t(k.label)}
            </label>
          ))}
        </div>
      </FilterGroup>

      {/* OFFERS — only if there are products on sale */}
      {meta.has_sale && (
        <FilterGroup title={t("Offers")}>
          <label className="flex items-center gap-3 cursor-pointer text-sm">
            <Checkbox checked={onSale} onCheckedChange={() => { setOnSale((v) => !v); setPage(1); }} data-testid="filter-on-sale" />
            {t("On Sale")}
          </label>
        </FilterGroup>
      )}

      {activeCount > 0 && (
        <Button variant="ghost" onClick={clearAll} className="mt-4 text-sm gap-2" data-testid="clear-filters-btn">
          <X className="h-4 w-4" /> {t("Clear all filters")}
        </Button>
      )}
    </div>
  );

  return (
    <div data-testid="catalog-page">
      {/* Header */}
      <div className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16">
          <p className="overline text-gold mb-3">{t("Fine Jewelry")}</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight capitalize">{t(title)}</h1>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10 flex gap-12">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-32">
            {filtersContent}
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-4 mb-8">
            <p className="text-sm text-muted-foreground" data-testid="result-count">
              {loading ? t("Loading…") : `${total} ${total === 1 ? t("piece") : t("pieces")}`}
            </p>
            <div className="flex items-center gap-3">
              <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="lg:hidden rounded-none" data-testid="mobile-filter-btn">
                    <SlidersHorizontal className="h-4 w-4 mr-2" /> {t("Filters")}
                    {activeCount > 0 && <span className="ml-2 bg-gold text-gold-foreground text-[10px] h-4 min-w-4 px-1 flex items-center justify-center rounded-full">{activeCount}</span>}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[320px] overflow-y-auto">
                  <SheetHeader><SheetTitle className="font-serif text-2xl text-left">{t("Filters")}</SheetTitle></SheetHeader>
                  {filtersContent}
                </SheetContent>
              </Sheet>
              <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
                <SelectTrigger className="w-[180px] rounded-none" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORTS.map((s) => (
                    <SelectItem key={s.value} value={s.value} data-testid={`sort-${s.value}`}>{t(s.label)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!loading && products.length === 0 ? (
            <div className="py-32 text-center" data-testid="empty-results">
              <p className="font-serif text-3xl mb-3">{t("Nothing found")}</p>
              <p className="text-muted-foreground">{t("Try adjusting your filters or search.")}</p>
              <Button onClick={clearAll} className="mt-6 rounded-none">{t("Reset filters")}</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10 lg:gap-8">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-16" data-testid="pagination">
              <Button variant="outline" size="icon" className="rounded-none" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <Button
                  key={p}
                  variant={p === page ? "default" : "outline"}
                  size="icon"
                  className="rounded-none"
                  onClick={() => setPage(p)}
                  data-testid={`page-${p}`}
                >
                  {p}
                </Button>
              ))}
              <Button variant="outline" size="icon" className="rounded-none" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="page-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
